import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import SLChangeOrderTable from './SLChangeOrderTable';
import {
  SubcontractCOFormFields,
  SubcontractCOFormItem,
  SubcontractCOItem,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import {
  fetchSLChangeOrderItems,
  fetchUnitsOfMeasure,
} from '../../../../services/accounting/ACFormsService';
import { fetchJCJobPhases } from '../../../../services/accounting/JobsContractsService';
import { useFormContext } from 'react-hook-form';
import { checkItem } from './util';
import ErrorToast, { showToast } from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';

type SLChangeOrderTableContainerProps = {
  subcontract: {
    SL: string;
    job: string;
    retainage: number;
  };
};

const SLChangeOrderTableContainer = ({
  subcontract,
}: SLChangeOrderTableContainerProps) => {
  const { selectedCompany } = useCompanyContext();
  const [items, setItems] = useState<SubcontractCOFormItem[]>([]);
  const {
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<SubcontractCOFormFields>();
  const baseItems = useRef<Record<number, SubcontractCOFormItem>>({});
  const toast = useRef<Toast>(null);

  const compareItems = (
    items: SubcontractCOFormItem[],
    backup: Record<number, SubcontractCOFormItem>
  ) => {
    return items.some((item) => {
      const buItem = backup[item.item];
      return (
        item.item !== buItem.item ||
        item.description !== buItem.description ||
        item.um !== buItem.um ||
        item.units !== buItem.units ||
        item.unitCosts !== buItem.unitCosts ||
        item.total !== buItem.total
      );
    });
  };

  useQuery({
    queryKey: ['getJCJobPhases', selectedCompany, subcontract.job],
    queryFn: ({ signal }) => {
      return fetchJCJobPhases(
        {
          companyID: selectedCompany!.id,
          job: subcontract.job,
          hasCost: false,
          costTypes: [3],
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && subcontract.job),
  });

  useQuery({
    queryKey: ['getUnitOfMeasure'],
    queryFn: ({ signal }) => {
      return fetchUnitsOfMeasure(signal);
    },
    refetchOnWindowFocus: false,
  });

  const { isFetching, isError } = useQuery({
    queryKey: ['getSubcontractItems', selectedCompany, subcontract.SL],
    queryFn: ({ signal }) => {
      setItems([]);

      return fetchSLChangeOrderItems(
        {
          companyID: selectedCompany!.id,
          sl: subcontract.SL,
        },
        signal
      );
    },
    onSuccess: (data) => {
      const items: SubcontractCOFormItem[] = data.map(
        (item: SubcontractCOItem) => ({
          ...item,
          retainage: item.retainage * 100,
          total: item.total,
        })
      );

      const backUpItems = localStorage.getItem('temp-sl-co-items');
      const backUpBaseItems = localStorage.getItem('temp-sl-co-items-original');
      const backUpCompany = localStorage.getItem('temp-sl-co-company');

      localStorage.removeItem('temp-sl-co-items');
      localStorage.removeItem('temp-sl-co-items-original');

      if (
        !backUpBaseItems ||
        !backUpItems ||
        backUpCompany !== selectedCompany!.id
      ) {
        baseItems.current = items.reduce((ac, item) => {
          ac[item.item] = { ...item };
          return ac;
        }, {} as Record<number, SubcontractCOFormItem>);

        setItems(items);
        return;
      }

      const buBaseItemsJson = JSON.parse(backUpBaseItems) as Record<
        number,
        SubcontractCOFormItem
      >;
      const hasDifference = compareItems(items, buBaseItemsJson);

      if (
        Object.keys(buBaseItemsJson).length === items.length &&
        !hasDifference
      ) {
        const buItemsJson = JSON.parse(backUpItems) as SubcontractCOFormItem[];
        setItems(buItemsJson);
        setValue('items', buItemsJson);
        baseItems.current = buBaseItemsJson;
      } else {
        setTimeout(() => {
          showToast(
            'error',
            toast,
            'Subcontract Change Order',
            'There were changes in the items compared to the previous version, so the backup was not used.',
            7000
          );

          baseItems.current = items.reduce((ac, item) => {
            ac[item.item] = { ...item };
            return ac;
          }, {} as Record<number, SubcontractCOFormItem>);

          setItems(items);
        }, 500);
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && subcontract.SL),
  });

  const updateBackup = (items: SubcontractCOFormItem[]) => {
    localStorage.setItem('temp-sl-co-items', JSON.stringify(items));
    localStorage.setItem(
      'temp-sl-co-items-original',
      JSON.stringify(baseItems.current)
    );
    localStorage.setItem('temp-sl-co-subcontract', subcontract.SL);
    localStorage.setItem('temp-sl-co-company', selectedCompany!.id);
  };

  useEffect(() => {
    if (errors.items) {
      setItems((items) => [...items]);
    }
  }, [errors.items]);

  if (isFetching) {
    return (
      <div className="text-center mx-auto mt-3">
        <ProgressSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorMessage
        content={'Failed to obtain data! Please try again later.'}
      />
    );
  }

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <SLChangeOrderTable
        items={subcontract.SL ? items : []}
        defaultItems={baseItems}
        job={subcontract.job}
        refreshItems={() => {
          setItems((items) => {
            let hasError = false;
            const newItems = items.map((item) => {
              hasError = hasError || !!checkItem(item);
              return item;
            });

            if (errors.items && !hasError) {
              clearErrors('items');
            }

            setValue('items', newItems);
            updateBackup(newItems);
            return newItems;
          });
        }}
        addItem={() => {
          setItems((items) => {
            if (!subcontract.SL) {
              return items;
            }

            const number = items[items.length - 1]?.item ?? 0;
            const newItems = [
              ...items,
              {
                item: number + 1,
                isNew: true,
                isEdited: false,
                retainage: subcontract.retainage * 100,
                pending: false,
              },
            ];
            setValue('items', newItems);
            updateBackup(newItems);
            return newItems;
          });
        }}
        resetItem={(item) => {
          const original = baseItems.current[item];
          setItems((items) => {
            const newItems = items.map((currentItem) =>
              currentItem.item === item ? { ...original } : currentItem
            );
            setValue('items', newItems);
            updateBackup(newItems);
            return newItems;
          });
        }}
        removeItem={(item) => {
          setItems((items) => {
            let wasRemoved = false;
            let hasError = false;
            const newItems = items.reduce((ac, currentItem) => {
              if (currentItem.item !== item) {
                if (wasRemoved) {
                  currentItem.item = currentItem.item - 1;
                }
                hasError = hasError || !!checkItem(currentItem);
                ac.push(currentItem);
              } else {
                wasRemoved = true;
              }

              return ac;
            }, [] as SubcontractCOFormItem[]);
            setValue('items', newItems);
            updateBackup(newItems);

            if (errors.items && !hasError) {
              clearErrors('items');
            }

            return newItems;
          });
        }}
        showErrors={!!errors.items}
      />
    </React.Fragment>
  );
};

export default SLChangeOrderTableContainer;
